import React, {
  Fragment,
} from 'react';
import startCase from 'lodash/startCase';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import {
  ColumnHeader,
} from 'dtos/columnHeader';
import {
  isNilOrEmpty,
  isPlainObject,
  safeString,
} from 'views/EMR/components/Eligibility/utils';
import clsx from 'clsx';

export type WildcardFieldValueProps = {
  value: unknown;
};

// WildcardFieldGroup is defined below; both are mutually recursive function declarations (hoisted)

export function WildcardFieldValue({ value }: WildcardFieldValueProps) {
  if (isPlainObject(value)) {
    const nested = Object.entries(value as Record<string, unknown>).filter(
      ([key, v]) => key !== 'uniqueId' && !isNilOrEmpty(v),
    );
    // eslint-disable-next-line no-use-before-define
    return <WildcardFieldGroup label="" fields={nested} />;
  }

  if (Array.isArray(value)) {
    const items = value as Record<string, unknown>[];
    if (items.every(isPlainObject)) {
      return (
        <div className="flex flex-col gap-1">
          {items.map((item) => (
            // eslint-disable-next-line no-use-before-define
            <WildcardFieldGroup
              key={item.uniqueId as string}
              fields={Object.entries(item).filter(
                ([key, itemValue]) => key !== 'uniqueId' && !isNilOrEmpty(itemValue),
              )}
            />
          ))}
        </div>
      );
    }
    return (
      <ul className="list-disc list-inside space-y-0.5">
        {(value as unknown[]).map((item, index) => (
          <li key={`${String(item) + index}`}>{safeString(item)}</li>
        ))}
      </ul>
    );
  }

  return <span className="font-medium text-gray-800">{safeString(value)}</span>;
}

export type WildcardFieldGroupProps = {
  label?: string;
  fields: [string, unknown][];
};

export function WildcardFieldGroup({ label = '', fields }: WildcardFieldGroupProps) {
  return (
    <div className="rounded-md py-1 text-xs">
      <div className="font-semibold text-gray-600 mb-1">{label}</div>
      {fields.map(([key, val], i) => {
        const isPrimitive = !isPlainObject(val) && !Array.isArray(val);
        return (
          <div key={key} className="flex flex-col">
            <div className="flex gap-2 items-start">
              <span className="text-gray-400 select-none font-light shrink-0">
                {i === fields.length - 1 ? '└─' : '├─'}
              </span>
              <span className="text-gray-500 w-24 shrink-0">{startCase(key)}</span>
              {isPrimitive && (
                <span className="min-w-0 flex-1">
                  <WildcardFieldValue value={val} />
                </span>
              )}
            </div>
            {!isPrimitive && (
              <div className={clsx('ml-[5px] pl-4 border-l border-gray-200', {
                'border-l-0': i === fields.length - 1,
              })}
              >
                <WildcardFieldValue value={val} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

type EligibilityWildcardProps = {
  value: Record<string, unknown>;
};

function EligibilityWildcard({ value }: EligibilityWildcardProps) {
  const entries = Object.entries(value).filter(
    ([key, entryValue]) => key !== 'uniqueId' && !isNilOrEmpty(entryValue),
  );

  const primitives = entries.filter(
    ([, entryValue]) => !isPlainObject(entryValue) && !Array.isArray(entryValue),
  );
  const nestedObjects = entries.filter(([, entryValue]) => isPlainObject(entryValue));
  const arrayOfObjects = entries.filter(
    ([, entryValue]) => Array.isArray(entryValue) && (entryValue as any[]).every(isPlainObject),
  );
  const arrayOfPrimitives = entries.filter(
    ([, entryValue]) => Array.isArray(entryValue) && !(entryValue as any[]).every(isPlainObject),
  );

  return (
    <div className="flex flex-col gap-2">
      {primitives.length > 0 && (
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          {primitives.map(([key, val]) => (
            <Fragment key={key}>
              <span className="font-medium text-gray-600">
                {startCase(key)}
                :
              </span>
              <span className="font-medium text-gray-800">{safeString(val)}</span>
            </Fragment>
          ))}
        </div>
      )}
      {nestedObjects.map(([key, val]) => (
        <WildcardFieldGroup
          key={key}
          label={startCase(key)}
          fields={
            Object.entries(val as Record<string, unknown>)
              .filter(([fieldKey, entryValue]) => fieldKey !== 'uniqueId' && !isNilOrEmpty(entryValue))
          }
        />
      ))}
      {arrayOfObjects.flatMap(([fieldKey, items]) => {
        const objectItems = items as Record<string, unknown>[];
        return objectItems.map((item, index) => (
          <WildcardFieldGroup
            key={`${fieldKey}-${String(item.uniqueId ?? index)}`}
            label={startCase(fieldKey)}
            fields={Object.entries(item).filter(
              ([itemKey, entryValue]) => itemKey !== 'uniqueId' && !isNilOrEmpty(entryValue),
            )}
          />
        ));
      })}
      {arrayOfPrimitives.map(([key, items]) => (
        <div key={key}>
          <span className="font-medium text-gray-600">
            {startCase(key)}
            :
          </span>
          <ul className="list-disc list-inside font-medium text-gray-800 space-y-0.5 mt-0.5">
            {(items as unknown[]).map((item, index) => (
              <li key={`${String(item) + index}`}>{safeString(item)}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

type EligibilityArrayProps = {
  value: unknown[];
};

function EligibilityArray({ value }: EligibilityArrayProps) {
  return (
    <ul className="list-disc list-inside font-medium text-gray-800 space-y-0.5">
      {value.map((item, index) => (
        <li key={`${String(item) + index}`}>{safeString(item)}</li>
      ))}
    </ul>
  );
}

export type EligibilityCellProps = {
  value: any;
  header: ColumnHeader;
};

export function EligibilityCell({ value, header }: EligibilityCellProps) {
  const isEmpty = isNilOrEmpty(value);
  const isActive = header.key === 'coverage' && value === 'Active Coverage';
  const isInactive = header.key === 'coverage' && value === 'Inactive';

  if (isEmpty) {
    return <span className="text-gray-500 font-medium">Not set</span>;
  }

  if (header.key === '*' && isPlainObject(value)) {
    return <EligibilityWildcard value={value} />;
  }

  if (Array.isArray(value)) {
    return <EligibilityArray value={value} />;
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([, entryValue]) => !isNilOrEmpty(entryValue));

    return (
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {entries.map(([key, entryValue], index) => (
          <Fragment key={key}>
            <span className="font-medium text-gray-600">
              {startCase(key)}
              :
            </span>
            <EligibilityCell value={entryValue} header={header} />
            {index < entries.length - 1 && (
              <div className="col-span-2 border-b border-gray-100 my-1" />
            )}
          </Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {isActive && <CheckCircleIcon className="text-green-600 !text-base" />}
      {isInactive && <DoNotDisturbOnIcon className="text-red-400 !text-base" />}
      <span className="text-gray-800 font-medium">{safeString(value)}</span>
    </div>
  );
}
